import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Stack, Group, Card, Table, Button, Badge, Text, Chip, ScrollArea,
  Center, Loader, Alert, Box,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Plus, Trophy, ChevronRight } from 'lucide-react';

import { gamesApi, gameTypesApi, groupsApi, participationApi, type Game } from '../api';
import { modeMeta } from '../components/modeMeta';
import { NewGameModal } from '../components/NewGameModal';
import { generateGameUuid } from '../utils/uuid';
import { useActiveEpoch } from '../hooks/useActiveEpoch';
import { numStyle } from '../theme';

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
}

export default function GamesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 48em)');
  const [filter, setFilter] = useState('all');
  const [creating, setCreating] = useState(false);

  // Hry se vždycky omezují na aktivní epochu — bez toho by seznam míchal tábory.
  const { epoch, isLoading: epochLoading } = useActiveEpoch();
  const games = useQuery({
    queryKey: ['games', epoch?.id],
    queryFn: () => gamesApi.getAll(epoch!.id),
    enabled: !!epoch,
  });
  const gameTypes = useQuery({ queryKey: ['gameTypes'], queryFn: gameTypesApi.getAll });
  const groups = useQuery({ queryKey: ['groups'], queryFn: groupsApi.getAll });

  const create = useMutation({
    mutationFn: async (data: { name: string; gameTypeId: string; groupIds: number[] }) => {
      const uuid = generateGameUuid();
      await gamesApi.create(data.name, uuid, data.gameTypeId);
      // Účast skupin se zapisuje zvlášť — bez ní by hra neměla pro koho počítat body.
      if (groups.data?.length) {
        await participationApi.setGroups(
          uuid,
          groups.data.map((g) => ({ groupId: g.id, isActive: data.groupIds.includes(g.id) }))
        );
      }
      return uuid;
    },
    onSuccess: (uuid) => {
      notifications.show({ message: 'Hra založena', color: 'forest' });
      setCreating(false);
      qc.invalidateQueries({ queryKey: ['games'] });
      navigate(`/hry/${uuid}`);
    },
    onError: (e: any) =>
      notifications.show({ title: 'Hru se nepodařilo založit', message: e?.response?.data?.error || e.message, color: 'red' }),
  });

  const list = useMemo(() => {
    const all = games.data ?? [];
    const sorted = [...all].sort((a, b) => {
      const aLive = a.ended_at ? 1 : 0;
      const bLive = b.ended_at ? 1 : 0;
      if (aLive !== bLive) return aLive - bLive;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    if (filter === 'all') return sorted;
    if (filter === 'live') return sorted.filter((g) => !g.ended_at);
    return sorted.filter((g) => g.game_type_id === filter);
  }, [games.data, filter]);

  if (epochLoading || (epoch && games.isLoading)) return <Center h={240}><Loader /></Center>;
  if (!epoch) {
    return (
      <Alert color="orange" title="Žádná aktivní epocha">
        Hry patří k epoše. Aktivuj ji v Nastavení → Epochy.
      </Alert>
    );
  }
  if (games.error) {
    return <Alert color="red" title="Hry se nepodařilo načíst">{(games.error as any)?.message}</Alert>;
  }

  const statusBadge = (g: Game) =>
    g.ended_at
      ? <Badge color="gray">ukončena</Badge>
      : <Badge color="flag" leftSection={<span className="live-dot" />}>běží</Badge>;

  return (
    <Stack gap="sm">
      <Group gap="sm">
        <ScrollArea type="never" style={{ flex: 1 }}>
          <Chip.Group multiple={false} value={filter} onChange={(v) => setFilter(v as string)}>
            <Group gap={5} wrap="nowrap">
              <Chip value="all" size="xs">Vše</Chip>
              <Chip value="live" size="xs">Běžící</Chip>
              {(gameTypes.data ?? []).map((t) => (
                <Chip key={t.id} value={t.id} size="xs">{t.name || modeMeta(t.id).label}</Chip>
              ))}
            </Group>
          </Chip.Group>
        </ScrollArea>
        <Button leftSection={<Plus size={14} />} onClick={() => setCreating(true)}>Nová hra</Button>
      </Group>

      <Card padding={0}>
        {list.length === 0 ? (
          <Stack align="center" py="xl" gap={4}>
            <Trophy size={22} color="var(--mantine-color-dimmed)" />
            <Text fw={600}>Žádná hra v tomto filtru</Text>
            <Text c="dimmed" fz="sm">Zkus jiný herní mód, nebo hru založ.</Text>
          </Stack>
        ) : isMobile ? (
          <Box>
            {list.map((g) => {
              const meta = modeMeta(g.game_type_id);
              const Icon = meta.icon;
              return (
                <Group
                  key={g.uuid}
                  p="sm"
                  gap="sm"
                  wrap="nowrap"
                  onClick={() => navigate(`/hry/${g.uuid}`)}
                  style={{ cursor: 'pointer', borderBottom: '1px solid var(--mantine-color-default-border)' }}
                >
                  <Icon size={16} color={`var(--mantine-color-${meta.color}-6)`} style={{ flex: '0 0 16px' }} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={650} fz="sm" truncate>{g.name}</Text>
                    <Text c="dimmed" fz="xs" style={numStyle}>{meta.label} · {when(g.created_at)}</Text>
                  </Box>
                  {statusBadge(g)}
                </Group>
              );
            })}
          </Box>
        ) : (
          <Table.ScrollContainer minWidth={620}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={30} />
                  <Table.Th>Hra</Table.Th>
                  <Table.Th w={130}>Mód</Table.Th>
                  <Table.Th w={150}>Začátek</Table.Th>
                  <Table.Th w={110}>Stav</Table.Th>
                  <Table.Th w={30} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.map((g) => {
                  const meta = modeMeta(g.game_type_id);
                  const Icon = meta.icon;
                  return (
                    <Table.Tr
                      key={g.uuid}
                      onClick={() => navigate(`/hry/${g.uuid}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Td><Icon size={16} color={`var(--mantine-color-${meta.color}-6)`} /></Table.Td>
                      <Table.Td fw={600}>{g.name}</Table.Td>
                      <Table.Td><Text fz="sm" c="dimmed">{meta.label}</Text></Table.Td>
                      <Table.Td><Text fz="sm" c="dimmed" style={numStyle}>{when(g.created_at)}</Text></Table.Td>
                      <Table.Td>{statusBadge(g)}</Table.Td>
                      <Table.Td><ChevronRight size={15} color="var(--mantine-color-dimmed)" /></Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      <NewGameModal
        opened={creating}
        gameTypes={gameTypes.data ?? []}
        groups={groups.data ?? []}
        saving={create.isPending}
        onClose={() => setCreating(false)}
        onSubmit={(data) => create.mutate(data)}
      />
    </Stack>
  );
}
